/**
 * PRP-WEB-2: the small dialog behind the menus' "…" items and double-click editing (a length,
 * a name, or a pick from a list). Enter applies, Escape cancels; a refusal shows under the field.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { formatFeetInches, parseFeetInches } from '../feetInches';
import type { PromptSpec } from './menuModel';

export function PromptDialog({ spec, onClose }: { spec: PromptSpec; onClose: () => void }) {
  const id = useId();
  const first = useRef<HTMLInputElement & HTMLSelectElement>(null);
  const [text, setText] = useState(spec.kind === 'length' ? formatFeetInches(spec.valueFt) : spec.value);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { first.current?.focus(); first.current?.select?.(); }, []);

  const apply = () => {
    let msg: string | void;
    if (spec.kind === 'length') {
      const ft = parseFeetInches(text);
      if (ft == null || ft <= 0) { setError(`Type feet and inches, like 12' 6" or 12.5`); return; }
      msg = spec.apply(ft);
    } else msg = spec.apply(text);
    if (msg) { setError(msg); return; }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4" data-testid="plan-prompt"
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form role="dialog" aria-modal="true" aria-labelledby={`${id}-t`}
        className="w-full max-w-[320px] space-y-3 rounded-[var(--r-lg)] border border-[var(--bd)] bg-[var(--bg-2)] p-4 shadow-[0_12px_32px_rgba(0,0,0,.3)]"
        onSubmit={(e) => { e.preventDefault(); apply(); }}
        onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); } }}>
        <label id={`${id}-t`} htmlFor={id} className="block text-[13px] font-[620] text-[var(--tx-hi)]">{spec.title}</label>
        {spec.kind === 'choice' ? (
          <select id={id} ref={first} data-testid="plan-prompt-input" value={text} onChange={(e) => setText(e.target.value)}
            className="h-[34px] w-full rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-inset)] px-2 text-[13px] text-[var(--tx-hi)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)]">
            {spec.choices.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        ) : (
          <Input id={id} ref={first} data-testid="plan-prompt-input" value={text} autoComplete="off"
            inputMode={spec.kind === 'length' ? 'decimal' : undefined}
            aria-invalid={error ? true : undefined} onChange={(e) => setText(e.target.value)} />
        )}
        {error && <p role="alert" className="text-[12px] text-[var(--bad)]">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" size="sm" variant="primary" data-testid="plan-prompt-ok">OK</Button>
        </div>
      </form>
    </div>
  );
}

export default PromptDialog;
